/**
 * /portal/tasks/:taskId — task detail (T-041 + T-042 approve + T-043
 * revision drawer).
 *
 * Renders, top to bottom:
 *  - the 5-step StatusTimeline (PR-53, DES-14) above the deliverable block
 *  - the StatusPill (never replaced by the timeline)
 *  - the brief
 *  - ClockBar (PR-10, PR-50) when the task is active and has a pull_time
 *  - WhatEricIsDoingPanel (PR-09) only on {pulled, in_progress, agent_draft,
 *    eric_reviewing}, grounded in >= 1 context-doc detail
 *  - the deliverable, embedded INLINE via the public_deliverables view +
 *    DeliverableLayout + the renderer registry (ARCH-005 §6/§7 — no iframe)
 *  - "Copy share link" -> /d/<task-id> with a toast (PR-19)
 *  - Approve action on delivered tasks (T-042 / PR-14)
 *  - "Request a revision" -> RevisionDrawer (T-043 / PR-54)
 */

import { useCallback, useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { Source, Task } from '@studio/api-schemas';
import { getCurrentSession } from '@/lib/auth';
import {
  fetchOwnContextDoc,
  fetchOwnSubscriber,
  fetchPublicDeliverable,
  fetchTaskById,
  fetchTaskSources,
  type PortalContextDoc,
  type PublicDeliverableView,
} from '@/lib/portal';
import { approveTask } from '@/lib/queries/task-detail';
import {
  PortalInputRequestCard,
  fetchTaskInputRequest,
  type RecipientInputRequest,
} from '@/components/input-requests';
import { AsyncPanel, type AsyncState } from '@/components/portal/AsyncPanel';
import { StatusPill } from '@/components/portal/StatusPill';
import { StatusTimeline } from '@/components/portal/StatusTimeline';
import { ClockBar } from '@/components/portal/ClockBar';
import { WhatEricIsDoingPanel } from '@/components/portal/WhatEricIsDoingPanel';
import { RevisionDrawer } from '@/components/portal/RevisionDrawer';
import {
  DeliverableLayout,
  type DeliverableMetaNote,
} from '@/components/portal/renderers/DeliverableLayout';
import {
  DeliverableBody,
  type Verbosity,
} from '@/components/portal/renderers/registry';
import { TASK_TYPE_LABEL } from '@/components/portal/task-type-labels';
import { toast } from '@/components/ui/toast';

export const Route = createFileRoute('/portal/tasks/$taskId')({
  component: TaskDetailPage,
});

interface DetailBundle {
  task: Task;
  subscriberId: string;
  tier: 'studio' | 'pro';
  accessToken: string;
  context: PortalContextDoc | null;
  deliverable: PublicDeliverableView | null;
  sources: Source[];
  inputRequest: RecipientInputRequest | null;
}

function TaskDetailPage() {
  const { taskId } = Route.useParams();
  const [state, setState] = useState<AsyncState<DetailBundle>>({
    status: 'loading',
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const [task, session, subscriber, context] = await Promise.all([
        fetchTaskById(taskId),
        getCurrentSession(),
        fetchOwnSubscriber(),
        fetchOwnContextDoc(),
      ]);
      if (!task) {
        setState({
          status: 'error',
          message: 'This task could not be found.',
        });
        return;
      }
      if (!session || !subscriber) {
        setState({
          status: 'error',
          message: 'Could not load your account. Sign in again if it persists.',
        });
        return;
      }
      const [deliverable, sources, inputRequest] = await Promise.all([
        task.status === 'delivered' || task.status === 'approved'
          ? fetchPublicDeliverable(taskId)
          : Promise.resolve(null),
        fetchTaskSources(taskId),
        // Surface C: the open/answered/closed input request for this task, if
        // any. A failed read degrades to no card rather than failing the page.
        fetchTaskInputRequest(taskId).catch(() => null),
      ]);
      setState({
        status: 'ready',
        data: {
          task,
          subscriberId: subscriber.id,
          tier: subscriber.tier === 'pro' ? 'pro' : 'studio',
          accessToken: session.access_token,
          context,
          deliverable,
          sources,
          inputRequest,
        },
      });
    } catch {
      setState({
        status: 'error',
        message: 'Could not load this task. Try again in a moment.',
      });
    }
  }, [taskId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleApprove(accessToken: string) {
    setApproving(true);
    const result = await approveTask(taskId, accessToken);
    if (result.ok) {
      toast.success('Approved. Thank you.');
      await load();
    } else {
      toast.error(result.message);
    }
    setApproving(false);
  }

  function copyShareLink() {
    const url = `${window.location.origin}/d/${taskId}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success('Share link copied.'))
      .catch(() =>
        toast.error('Could not copy. The link is /d/' + taskId),
      );
  }

  return (
    <AsyncPanel state={state} loadingLabel="Loading this task...">
      {(d) => {
        const isDelivered =
          d.task.status === 'delivered' || d.task.status === 'approved';
        const verbosity: Verbosity =
          (d.task.coaching_depth as Verbosity | null) ?? 'show_me';
        const metaNote: DeliverableMetaNote | null = d.deliverable
          ? {
              skeleton: d.deliverable.meta_skeleton,
              voice_decisions: d.deliverable.meta_voice_decisions,
              what_left_out: d.deliverable.meta_what_left_out,
              do_yourself_next_time: d.deliverable.meta_do_yourself_next_time,
              sources_line: d.deliverable.meta_sources_line,
            }
          : null;

        return (
          <div className="max-w-[800px] space-y-8">
            <div className="flex items-center gap-2 text-sm">
              <Link
                to="/portal"
                className="text-[color:var(--color-text-mute)] underline-offset-4 hover:underline"
              >
                ← Back to backlog
              </Link>
              <span className="text-[color:var(--color-gray-300)]">/</span>
              <span className="font-mono text-[color:var(--color-black)]">
                Task {taskId.slice(0, 8)}
              </span>
              <span className="ml-auto">
                <StatusPill status={d.task.status} />
              </span>
            </div>

            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
                {TASK_TYPE_LABEL[d.task.type]} task
              </p>
              <h1 className="font-display text-4xl italic leading-[1.05] text-[color:var(--color-black)]">
                {TASK_TYPE_LABEL[d.task.type]}.
              </h1>
            </div>

            <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-5 lg:p-6">
              <StatusTimeline status={d.task.status} />
            </div>

            {/* Surface C (PRD-005 / ARCH-028, IR-T-09): the task input-request
                card, sat above the brief/deliverable. Absent when there is no
                request (PortalInputRequestCard returns null). */}
            <PortalInputRequestCard
              request={d.inputRequest}
              taskRef={`Task ${taskId.slice(0, 8)}`}
            />

            <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-5 lg:p-6">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
                Your brief
              </p>
              <p className="whitespace-pre-line text-base leading-relaxed text-[color:var(--color-black)]">
                {d.task.brief}
              </p>
            </div>

            {!isDelivered && d.task.pull_time ? (
              <ClockBar
                tier={d.tier}
                pullTime={d.task.pull_time}
                status={d.task.status}
              />
            ) : null}

            <WhatEricIsDoingPanel
              status={d.task.status}
              contextSections={d.context?.sections ?? null}
            />

            {isDelivered && d.deliverable ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-xl italic text-[color:var(--color-black)]">
                    Your deliverable
                  </h2>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={copyShareLink}
                      className="inline-flex items-center justify-center gap-1.5 rounded-md border-[1.5px] border-[color:var(--color-line-dark)] bg-transparent px-3.5 py-1.5 text-sm font-medium text-[color:var(--color-black)] transition-colors hover:bg-[color:var(--color-linen)]"
                    >
                      Copy share link
                    </button>
                    <button
                      type="button"
                      onClick={() => setDrawerOpen(true)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-md border-[1.5px] border-[color:var(--color-line-dark)] bg-transparent px-3.5 py-1.5 text-sm font-medium text-[color:var(--color-black)] transition-colors hover:bg-[color:var(--color-linen)]"
                    >
                      Request a revision
                    </button>
                  </div>
                </div>

                <DeliverableLayout
                  body={
                    <DeliverableBody
                      content={d.deliverable.content}
                      verbosity={verbosity}
                    />
                  }
                  metaNote={metaNote}
                  sources={d.sources}
                  videoUrl={d.deliverable.video_url}
                  isPro={d.deliverable.subscriber_tier === 'pro'}
                />

                {d.task.status === 'delivered' ? (
                  <div className="flex flex-wrap items-center gap-3 rounded-lg bg-[color:var(--color-linen)] px-5 py-3.5">
                    <p className="flex-1 text-sm text-[color:var(--color-black)]">
                      Happy with this? Approving frees the slot so the next
                      task gets pulled.
                    </p>
                    <button
                      type="button"
                      disabled={approving}
                      onClick={() => handleApprove(d.accessToken)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[color:var(--color-rose)] px-3.5 py-1.5 text-sm font-medium text-[color:var(--color-navy)] transition-colors hover:bg-[color:var(--color-rose-dark)] disabled:opacity-45"
                    >
                      {approving ? 'Approving...' : '✓ Approve'}
                    </button>
                  </div>
                ) : (
                  <div className="rounded-lg border-[1.5px] border-[color:var(--color-rose)] bg-[color:var(--color-rose-subtle2)] px-5 py-3.5">
                    <p className="text-sm font-semibold text-[color:var(--color-navy)]">
                      Approved. It is yours.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-[color:var(--color-text-mute)]">
                The deliverable will appear here the moment it is delivered.
              </p>
            )}

            <RevisionDrawer
              open={drawerOpen}
              onOpenChange={setDrawerOpen}
              taskId={taskId}
              subscriberId={d.subscriberId}
              deliverableSections={null}
              onSubmitted={() => void load()}
            />
          </div>
        );
      }}
    </AsyncPanel>
  );
}
